import React, { useState, useEffect, useMemo } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import type { ThermalModelConfig } from "../../../../../../../Missions/Satellites/SatelliteProfile/SatelliteDesigner/ThermalDesigner/types/ThermalConfig";
import { SatGizmoAxes as SatGizmo } from "../../../../../../../Missions/Satellites/SatelliteProfile/SatelliteDesigner/ThermalDesigner/components/visualization";
import CustomToolTip from "../../../../../../../CommonComponents/CustomToolTip";
import { PlayPauseSvg } from "../../../../../../../CommonComponents/timeline/Svg";
import { GraphSvg } from "../controls/ThermalHeatMapSvgs";
import ThermalHeatMap3dLayersBtn, { HeatmapLayerState } from "../controls/ThermalHeatMap3dLayersBtn";
import ThermalRenderingQualityBtn, { RenderingQualitySettings } from "../controls/ThermalRenderingQualityBtn";
import { HeatmapRow, formatTime } from "../../ThermalHeatMap";
import { FACE_ORDER, LEGEND_BG, EMPTY_NODES } from "../../constants/thermalConstants";
import { useThrottledValue, useSyncedCameraState } from "../../hooks/CubeAndFaceHooks";
import FloorDotGrid from "./FloorDotGrid";
import { CubeWireframe, CubeAxes, FaceBlend, FaceGhost, FaceGrid, InternalDecksHeatmap, DeckLabels } from "./CubeAndFaceComponents";

export interface ThermalHeatMap3dViewProps {
    heatmapRows: HeatmapRow[];
    stepIndex: number;
    nodesPerFace: number;
    globalMin: number;
    globalMax: number;
    isPlaying: boolean;
    isLastStep: boolean;
    playbackSpeed: number;
    isSynced: boolean;
    viewMode?: "normal" | "compact" | "fullscreen";
    thermalModel?: ThermalModelConfig;
    onToggleSync: () => void;
    onTogglePlay: () => void;
    onCycleSpeed: () => void;
    onToggle2d3d: () => void;
}

// ---------------------------------------------------------------------------
// Helper component to manage camera sync for each canvas
// Uses useThree to access the camera and registers it with the sync hook
const CameraSyncManager: React.FC<{
    mode: "external" | "internal";
    syncCameras?: boolean;
    cameraSync?: {
        registerCamera: (id: string, camera: THREE.Camera, invalidate: () => void) => void;
        unregisterCamera: (id: string) => void;
        syncCameraState: (sourceId: string) => void;
    };
}> = ({ mode, syncCameras, cameraSync }) => {
    const { camera, invalidate } = useThree();
    const orbitControlsRef = React.useRef<any>(null);
    const pendingSyncRef = React.useRef(false);

    // Register camera on mount
    useEffect(() => {
        if (syncCameras && cameraSync) {
            cameraSync.registerCamera(mode, camera, invalidate);
            return () => {
                cameraSync.unregisterCamera(mode);
            };
        }
    }, [mode, syncCameras, cameraSync, camera, invalidate]);

    // Set up OrbitControls change listener for syncing
    useEffect(() => {
        if (!orbitControlsRef.current || !syncCameras || !cameraSync) return;

        const controls = orbitControlsRef.current;
        const handleChange = () => {
            // Invalidate this canvas and batch sync updates using microtask queue
            invalidate();
            if (!pendingSyncRef.current) {
                pendingSyncRef.current = true;
                Promise.resolve().then(() => {
                    cameraSync.syncCameraState(mode);
                    pendingSyncRef.current = false;
                });
            }
        };

        controls.addEventListener('change', handleChange);
        return () => {
            controls.removeEventListener('change', handleChange);
        };
    }, [mode, syncCameras, cameraSync, invalidate]);

    return (
        <OrbitControls 
            ref={orbitControlsRef}
            enablePan={false} 
            makeDefault 
        />
    );
};

// ---------------------------------------------------------------------------
// Shared Three.js scene contents — used by both canvases in dual mode
// ---------------------------------------------------------------------------
interface CubeSceneProps {
    currentNodes: Record<string, number>;
    nodesPerFace: number;
    globalMin: number;
    globalMax: number;
    showNodeTemp: boolean;
    showNadir: boolean;
    showRollPitchYaw: boolean;
    showGizmo: boolean;
    showGrid: boolean;
    showSubsystems: boolean;
    showDecks: boolean;
    showFloorGrid: boolean;
    gizmoScale: number;
    mode: "external" | "internal";
    thermalModel?: ThermalModelConfig;
    syncCameras?: boolean;
    cameraSync?: {
        registerCamera: (id: string, camera: THREE.Camera, invalidate: () => void) => void;
        unregisterCamera: (id: string) => void;
        syncCameraState: (sourceId: string) => void;
    };
}

// Map dprIndex to dpr range: 0=Low(1), 1=Medium(1.5), 2=High(2)
const DPR_VALUES = [[1, 1] as [number, number], [1, 1.5] as [number, number], [1, 2] as [number, number]];

// Shared canvas configuration builder — on-demand rendering to avoid redundant full-scene redraws
const getCanvasProps = (settings: RenderingQualitySettings) => ({
    camera: { position: [0.5, 1.8, 2.4] as [number, number, number], fov: 45 },
    gl: { antialias: settings.antialiasing, alpha: true },
    style: { background: "transparent" } as React.CSSProperties,
    frameloop: "demand" as const,
    dpr: DPR_VALUES[settings.dprIndex],
});

const CubeScene: React.FC<CubeSceneProps> = React.memo(({
    currentNodes, nodesPerFace, globalMin, globalMax,
    showNodeTemp, showNadir, showRollPitchYaw, showGizmo, showGrid, showSubsystems, showDecks, showFloorGrid, gizmoScale, mode, thermalModel, syncCameras = false,
    cameraSync,
}) => {
    const anyAxisVisible = showNadir || showRollPitchYaw;
    return (
        <>
            <ambientLight intensity={1.5} />
            {showFloorGrid && <FloorDotGrid floorY={-0.65} extent={2.5} step={0.22} dotSize={0.15} />}
            <CubeWireframe />
            {anyAxisVisible && <CubeAxes showNadir={showNadir} showRollPitchYaw={showRollPitchYaw} />}
            {mode === "external"
                ? FACE_ORDER.map((face) => (
                    <FaceBlend key={face} faceId={face} nodes={currentNodes} nodesPerFace={nodesPerFace} globalMin={globalMin} globalMax={globalMax} showNodeTemp={showNodeTemp} />
                ))
                : FACE_ORDER.map((face) => <FaceGhost key={face} faceId={face} />)
            }
            {mode === "external" && showGrid && FACE_ORDER.map((face) => (
                <FaceGrid key={`grid-${face}`} faceId={face} nodesPerFace={nodesPerFace} />
            ))}
            {mode === "internal" && (
                <InternalDecksHeatmap
                    nodes={currentNodes}
                    nodesPerFace={nodesPerFace}
                    globalMin={globalMin}
                    globalMax={globalMax}
                    showNodeTemp={showNodeTemp}
                    showGrid={showGrid}
                    showSubsystems={showSubsystems}
                    thermalModel={thermalModel}
                />
            )}
            {mode === "internal" && showDecks && <DeckLabels />}
            {syncCameras ? (
                <CameraSyncManager
                    mode={mode}
                    syncCameras={syncCameras}
                    cameraSync={cameraSync}
                />
            ) : (
                <OrbitControls enablePan={false} />
            )}
            {showGizmo && <SatGizmo margin={[45, 48]} scale={gizmoScale} />}
        </>
    );
});

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------

const ThermalHeatMap3dView: React.FC<ThermalHeatMap3dViewProps> = React.memo(({
    heatmapRows,
    stepIndex,
    nodesPerFace,
    globalMin,
    globalMax,
    isPlaying,
    isLastStep,
    playbackSpeed,
    isSynced,
    viewMode = "normal",
    thermalModel,
    onToggleSync,
    onTogglePlay,
    onCycleSpeed,
    onToggle2d3d,
}) => {
    const qualityBtnRef = React.useRef<HTMLDivElement>(null);
    const layersBtnRef = React.useRef<HTMLDivElement>(null);

    const handleQualityOpen = React.useCallback(() => {
        const layersBtn = layersBtnRef.current?.querySelector('button.active');
        if (layersBtn) {
            (layersBtn as HTMLButtonElement).click();
        }
    }, []);

    const handleLayersOpen = React.useCallback(() => {
        const qualityBtn = qualityBtnRef.current?.querySelector('button.active');
        if (qualityBtn) {
            (qualityBtn as HTMLButtonElement).click();
        }
    }, []);

    const [renderingSettings, setRenderingSettings] = useState<RenderingQualitySettings>({
        dprIndex: 1,
        antialiasing: true,
        floorGridEnabled: true,
        refreshRateMs: 10000,
    });

    const isCompactMode = viewMode === "compact";
    const isDualMode = viewMode === "fullscreen";
    const [{ showGizmo, showNadir, showRollPitchYaw, showInternalFaces, showGrid, showDecks, showNodeTemp, showSubsystems, syncCameras }, setLayerState] =
        useState<HeatmapLayerState>({
            showGizmo: !isCompactMode,
            showNadir: false,
            showRollPitchYaw: false,
            showInternalFaces: false,
            showGrid: false,
            showDecks: false,
            showNodeTemp: false,
            showSubsystems: false,
            syncCameras: true,
        });

    // In compact mode the gizmo is always off regardless of layer state
    const effectiveShowGizmo = isCompactMode ? false : showGizmo;

    // Camera sync state for dual-view fullscreen mode
    const cameraSync = useSyncedCameraState(isDualMode && (syncCameras ?? true));

    const gizmoScale   = viewMode === "fullscreen" ? 36 : viewMode === "compact" ? 24 : 30;
    const currentNodes = heatmapRows[stepIndex]?.nodes ?? EMPTY_NODES;
    const timeSec      = heatmapRows[stepIndex]?.time_sec ?? 0;

    // Throttle 3D view updates to reduce GPU pressure during sync/playback
    const throttledNodes = useThrottledValue(currentNodes, renderingSettings.refreshRateMs);

    const sceneProps = useMemo<Omit<CubeSceneProps, "mode">>(() => ({
        currentNodes: throttledNodes, nodesPerFace, globalMin, globalMax,
        showNodeTemp, showNadir, showRollPitchYaw, showGizmo: effectiveShowGizmo, showGrid, showSubsystems, showDecks, showFloorGrid: renderingSettings.floorGridEnabled, gizmoScale, thermalModel,
        syncCameras: isDualMode && syncCameras,
        cameraSync: isDualMode ? cameraSync : undefined,
    }), [throttledNodes, nodesPerFace, globalMin, globalMax, showNodeTemp, showNadir, showRollPitchYaw,
         effectiveShowGizmo, showGrid, showSubsystems, showDecks, renderingSettings.floorGridEnabled, gizmoScale, thermalModel, isDualMode, syncCameras, cameraSync]);

    return (
        <div className="thm-3d-view">
            <div className="thm-3d-main-row">
                <div className="thm-3d-legend-col">
                    <span className="thm-3d-legend-label">{globalMax.toFixed(1)}°C</span>
                    <div className="thm-3d-legend-bar" style={{ background: LEGEND_BG }} />
                    <span className="thm-3d-legend-label">{globalMin.toFixed(1)}°C</span>
                </div>

                {isDualMode ? (
                    // Fullscreen dual-canvas: external (left) + internal (right)
                    <div className="thm-3d-dual-canvas-wrapper">
                        <div className="thm-3d-dual-pane">
                            <span className="thm-3d-dual-label">Faces View</span>
                            <Canvas {...getCanvasProps(renderingSettings)}>
                                <CubeScene {...sceneProps} mode="external" />
                            </Canvas>
                        </div>
                        <div className="thm-3d-dual-divider" />
                        <div className="thm-3d-dual-pane">
                            <span className="thm-3d-dual-label">Decks View</span>
                            <Canvas {...getCanvasProps(renderingSettings)}>
                                <CubeScene {...sceneProps} mode="internal" />
                            </Canvas>
                        </div>
                    </div>
                ) : (
                    // Single canvas (normal / compact / fullscreen without internal)
                    <div className="thm-3d-canvas-wrapper">
                        <Canvas {...getCanvasProps(renderingSettings)}>
                            <CubeScene
                                {...sceneProps}
                                mode={showInternalFaces ? "internal" : "external"}
                            />
                        </Canvas>
                    </div>
                )}

                {/* Right: playback controls */}
                <div className="thm-3d-playback-col">
                    <CustomToolTip title={isSynced ? "Unsync from Cesium clock" : "Sync to Cesium clock"} placement="left">
                        <button
                            className={`thm-pb-btn thm-pb-sync${isSynced ? " active" : ""}`}
                            onClick={onToggleSync}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 8 8" fill="none">
                                <circle cx="4" cy="4" r="4" fill="currentColor" />
                            </svg>
                        </button>
                    </CustomToolTip>
                    <div className="thm-pb-play-speed-group">
                        <CustomToolTip title={isPlaying ? "Pause" : isLastStep ? "Replay" : "Play"} placement="left">
                            <button className="thm-pb-btn thm-pb-play" disabled={isSynced} onClick={onTogglePlay}>
                                <PlayPauseSvg state={isPlaying ? "play" : "pause"} />
                            </button>
                        </CustomToolTip>
                        <CustomToolTip title="Cycle playback speed" placement="left">
                            <button className="thm-pb-btn thm-pb-speed" disabled={isSynced} onClick={onCycleSpeed}>
                                {playbackSpeed}×
                            </button>
                        </CustomToolTip>
                    </div>
                    <CustomToolTip title="Switch to Charts view" placement="left">
                        <button className="thm-pb-btn thm-pb-view-toggle" onClick={onToggle2d3d}>
                            <GraphSvg />
                        </button>
                    </CustomToolTip>
                    <div ref={qualityBtnRef}>
                        <ThermalRenderingQualityBtn
                            settings={renderingSettings}
                            onSettingsChange={setRenderingSettings}
                            onOpen={handleQualityOpen}
                        />
                    </div>
                    <div ref={layersBtnRef}>
                        <ThermalHeatMap3dLayersBtn
                            onLayersChange={setLayerState}
                            hideInternalFaces={viewMode === "fullscreen"}
                            hideGizmo={viewMode === "compact"}
                            isFullscreen={isDualMode}
                            onOpen={handleLayersOpen}
                        />
                    </div>
                </div>
            </div>

            {/* Step info */}
            <div className="thm-3d-info-row">
                <span>Elapsed time : {formatTime(timeSec)}</span>
            </div>

        </div>
    );
});

export default ThermalHeatMap3dView;
